/* eslint-disable no-unused-vars */
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';

import { useForm } from 'react-hook-form';
import { PROPOSAL_STATUSES, TOAST } from 'constant';
import { toast } from 'layout';
import { mapError } from 'utils/func';

const defaultControlValue = {
  mode: 'delete',
  mutate: null,
  value: { id: null, englishName: null, vietnameseName: null },
};

const UpdateStatusDialog = forwardRef(({ refetch }, ref) => {
  // #region Data
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [projectProposalControl, setProjectProposalControl] = useState(defaultControlValue);
  const { mode, mutate, value } = projectProposalControl;
  const [status, setStatus] = useState(null);
  const [note, setNote] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const statuses = [
    { name: 'Mới (Cho phép chỉnh sửa)', code: PROPOSAL_STATUSES.NEW },
    { name: 'Đã nộp', code: PROPOSAL_STATUSES.SUBMITTED },
    { name: 'Duyệt', code: PROPOSAL_STATUSES.APPROVED },
    { name: 'Từ chối', code: PROPOSAL_STATUSES.REJECTED },
  ];

  // #endregion

  // #region Event
  useImperativeHandle(
    ref,
    () => ({
      open: (_projectProposalControl) => {
        setProjectProposalControl({ ..._projectProposalControl });
        setVisible(true);
        const selectedStatus = statuses.find(
          ({ code }) => code === _projectProposalControl.value.status
        );
        setStatus(selectedStatus);
        setNote('');
      },
    }),
    []
  );

  const footerConfirmationSubmit = () => (
    <div>
      <Button
        label={t('projectProposal.cancel')}
        className="w-8rem"
        severity="danger"
        onClick={() => {
          setVisible(false);
        }}
        autoFocus
      />
      <Button
        label={t('projectProposal.ok')}
        className="w-8rem"
        severity="info"
        loading={isLoading}
        disabled={mode === 'updateStatus' && !status}
        onClick={() => {
          if (!value.id) return;
          const data = {
            id: value.id,
            ...(mode === 'updateStatus' && { data: { status: status.code, note } }),
          };
          setIsLoading(true);

          mutate(data, {
            onSuccess: () => {
              toast(TOAST.SUCCESS, t(`proposalList.${mode}.success`));
              setVisible(false);
              setTimeout(() => {
                setIsLoading(false);
                refetch();
              }, 50);
            },
            onError: (error) => {
              toast(TOAST.ERROR, mapError(t, error));
              setIsLoading(false);
            },
          });
        }}
      />
    </div>
  );
  // #endregion

  return (
    <Dialog
      header={
        <p className={`text-${mode === 'delete' ? 'red' : 'yellow'}-500`}>
          {t(`proposalList.${mode}.title`)}
        </p>
      }
      visible={visible}
      position="center"
      style={{ width: mode === 'updateStatus' ? '60%' : '500px' }}
      onHide={() => {
        setProjectProposalControl(defaultControlValue);
        setVisible(false);
      }}
      draggable={false}
      footer={footerConfirmationSubmit}
    >
      <>
        <p className="text-lg font-bold">
          {t('proposalList.label')}:{' '}
          {t('lang') === 'vi' ? value?.vietnameseName : value?.englishName}
        </p>
        {mode === 'updateStatus' && (
          <>
            <p className="text-base"> {t('proposal.updateStatus.selectStatus')}</p>
            <Dropdown
              value={status}
              onChange={(e) => setStatus(e.value)}
              options={statuses}
              optionLabel="name"
              placeholder="Select a Status"
              className="w-6 mb-3"
            />
            <p className="text-base">{t('proposal.history.note')}</p>
            <InputTextarea
              value={note}
              rows={8}
              onChange={(e) => setNote(e.target.value)}
              className="w-full mb-1"
            />
          </>
        )}
        {mode === 'delete' && (
          <p className="text-lg">{t(`proposalList.${mode}.confirmationMessage`)}</p>
        )}
      </>
    </Dialog>
  );
});

export default UpdateStatusDialog;

UpdateStatusDialog.propTypes = {
  refetch: PropTypes.func.isRequired,
};
